/**
 * SettleLandingPage — iter125.
 *
 * Route: /s/:encoded
 *
 * Lands here when someone scans a SplitEasy QR with their phone's NATIVE
 * camera (Google Lens / iOS Camera). Decodes the base64 payload and:
 *   - logged-in: jumps straight into the relevant settle sheet
 *   - logged-out: shows a friendly preview + login CTA
 *   - invalid:   shows a clear error + home link
 */
import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Wallet, AlertTriangle, ArrowRight, Smartphone, Download, Share2,
  CheckCircle2, Sparkles, Zap, ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { parseQrText } from '../lib/qrPayload';
import { formatNXC } from '../lib/formatNXC';
import QrSettleSheet from '../components/QrSettleSheet';
import PayByPhoneSheet from '../components/PayByPhoneSheet';
import api from '../lib/api';
import { toast } from 'sonner';

export default function SettleLandingPage() {
  const { encoded } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [decoded, setDecoded] = useState(null);
  const [parseError, setParseError] = useState(false);
  const [settling, setSettling] = useState(false);
  const [phoneSheetOpen, setPhoneSheetOpen] = useState(false);

  useEffect(() => {
    if (!encoded) { setParseError(true); return; }
    // Reconstruct the full URL we'd have generated and run it through the parser.
    const fullUrl = `${window.location.origin}/s/${encoded}`;
    const obj = parseQrText(fullUrl);
    if (!obj) { setParseError(true); return; }
    setDecoded(obj);
  }, [encoded]);

  // For per-debt settle QRs (group + amount baked in) when logged in: fire the
  // POST directly and bounce to the group's ledger. Single confirmation only.
  const handleQuickSettle = async () => {
    if (!decoded || decoded.kind !== 'spliteasy_settle') return;
    setSettling(true);
    try {
      const res = await api.post('/coin-transfers/qr-settle', {
        group_id: decoded.group_id,
        to_user_id: decoded.to_user_id,
        amount: decoded.amount,
        note: decoded.note,
      });
      toast.success(res.data?.message || 'Settled');
      navigate(`/groups/${decoded.group_id}?tab=ledger`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to settle');
      setSettling(false);
    }
  };

  if (parseError) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <Card className="p-6 rounded-2xl border-2 max-w-md text-center space-y-3" data-testid="settle-landing-error">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
          <h1 className="font-display text-xl font-bold">QR code unreadable</h1>
          <p className="text-sm text-muted-foreground">
            This doesn't look like a valid SplitEasy QR. Ask the person to regenerate it from their Profile.
          </p>
          <Button onClick={() => navigate('/')} className="rounded-full" data-testid="settle-landing-error-home">
            Go home <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Card>
      </div>
    );
  }

  if (!decoded) return null;

  // Logged-out preview — viral install funnel. Shows who's asking + Install + Sign in.
  if (!user) {
    const returnPath = encodeURIComponent(`/s/${encoded}`);
    const targetName = decoded.kind === 'spliteasy_settle'
      ? (decoded.to_name || 'Member')
      : (decoded.name || 'Member');
    const amountPreview = decoded.kind === 'spliteasy_settle' && decoded.amount > 0
      ? decoded.amount
      : null;
    const initials = (targetName || '?').split(/\s+/).filter(Boolean).slice(0, 2)
      .map((w) => w[0]).join('').toUpperCase();
    return <LoggedOutScanLanding
      targetName={targetName}
      initials={initials}
      amount={amountPreview}
      note={decoded.note}
      returnPath={returnPath}
    />;
  }

  // Logged-in flows
  if (decoded.kind === 'spliteasy_settle') {
    // Per-debt QR: confirm + 1-tap settle
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4 py-6">
        <Card className="p-6 rounded-2xl border-2 max-w-md w-full space-y-4" data-testid="settle-landing-settle">
          <h1 className="font-display text-xl font-bold text-center">
            Settle with {decoded.to_name || 'Member'}
          </h1>
          {decoded.amount > 0 && (
            <p className="text-center font-display text-3xl font-bold text-primary tabular-nums" data-testid="settle-landing-amount">
              {formatNXC(decoded.amount)}
            </p>
          )}
          {decoded.note && (
            <p className="text-xs text-muted-foreground italic text-center">"{decoded.note}"</p>
          )}
          <Button
            onClick={handleQuickSettle}
            disabled={settling}
            className="rounded-full w-full"
            data-testid="settle-landing-confirm"
          >
            {settling ? 'Settling…' : 'Confirm settle'}
          </Button>
          <Button variant="outline" onClick={() => navigate('/')} className="rounded-full w-full" data-testid="settle-landing-cancel">
            Not now
          </Button>
        </Card>
      </div>
    );
  }

  // Personal QR (no group/amount): jump into PayByPhoneSheet pre-filled by user_id.
  // We don't have the phone, but we do have user_id — drop into PayByPhoneSheet
  // pre-filled lookup-by-user-id flow. For simplicity, fall back to opening it
  // empty so the user can either type the amount or use the group dropdown.
  // We use a custom prefill that includes name + user_id so the sheet can skip
  // the lookup.
  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-6">
      <Card className="p-6 rounded-2xl border-2 max-w-md w-full space-y-3 text-center" data-testid="settle-landing-user">
        <div className="w-14 h-14 mx-auto rounded-full bg-primary/15 flex items-center justify-center">
          <Smartphone className="w-7 h-7 text-primary" />
        </div>
        <h1 className="font-display text-xl font-bold">Pay {decoded.name || 'Member'}</h1>
        <p className="text-sm text-muted-foreground">
          Pick which group + how much to settle. Their ledger updates instantly.
        </p>
        <Button
          onClick={() => setPhoneSheetOpen(true)}
          className="rounded-full w-full"
          data-testid="settle-landing-open-pay-sheet"
        >
          Continue <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
        <Button variant="outline" onClick={() => navigate('/')} className="rounded-full w-full">
          Not now
        </Button>

        {/* Pre-fill the phone-pay sheet by the user_id we already have. The
            sheet's normal flow expects a phone; we bypass by stashing the
            decoded payload as a synthetic prefill the sheet recognises. */}
        <PayByPhoneSheet
          open={phoneSheetOpen}
          onOpenChange={(o) => { setPhoneSheetOpen(o); if (!o) navigate('/', { replace: true }); }}
          prefill={{ userIdDirect: decoded.user_id, nameDirect: decoded.name }}
        />
      </Card>
    </div>
  );
}


/* ============================================================== */
/* Logged-out viral landing — turn every scan into an install funnel */
/* ============================================================== */
function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function LoggedOutScanLanding({ targetName, initials, amount, note, returnPath }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const ios = isIOS();
  const formattedAmount = amount != null
    ? new Intl.NumberFormat('en-IN').format(amount)
    : null;
  const captured = useRef(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      captured.current = true;
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      if (outcome === 'accepted') {
        // After install, the user will need to re-open the URL; ServiceWorker
        // remembers nothing about this page. Nudge them to sign in next.
      }
    } else if (ios) {
      setShowIosHint(true);
    } else {
      // Desktop Chrome where prompt didn't fire — fallback message.
      setShowIosHint(true);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-background px-4 py-6 flex flex-col items-center">
      {/* SplitEasy brand mini-header */}
      <div className="w-full max-w-md flex items-center justify-between mb-6">
        <div className="inline-flex items-center gap-2">
          <img src="/icon-192.png" alt="SplitEasy" className="w-7 h-7 rounded-md" />
          <span className="font-display font-bold text-lg">SplitEasy</span>
        </div>
        <Link to={`/login?redirect=${returnPath}`} className="text-xs font-semibold text-muted-foreground hover:text-foreground" data-testid="scan-landing-signin-link">
          Sign in
        </Link>
      </div>

      {/* Hero scan-target card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
        data-testid="scan-landing-logged-out"
      >
        <Card className="p-6 rounded-3xl border-2 text-center space-y-3 bg-card/95 backdrop-blur shadow-xl">
          <div className="w-20 h-20 mx-auto rounded-full bg-primary/15 border-4 border-primary/30 flex items-center justify-center font-display font-bold text-2xl shadow-inner" data-testid="scan-landing-initials">
            {initials || '?'}
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold">
              {amount ? "wants you to settle" : "wants to settle with you"}
            </p>
            <h1 className="font-display text-2xl font-bold mt-0.5">{targetName}</h1>
          </div>
          {formattedAmount && (
            <p className="font-display text-4xl font-black tabular-nums text-primary" data-testid="scan-landing-amount">
              {formattedAmount} <span className="text-base font-bold opacity-70">₹</span>
            </p>
          )}
          {note && (
            <p className="text-xs text-muted-foreground italic">"{note}"</p>
          )}
        </Card>
      </motion.div>

      {/* Install CTA */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.15 }}
        className="w-full max-w-md mt-5"
      >
        <Card className="p-5 rounded-3xl border-2 border-primary/30 bg-gradient-to-br from-primary/5 via-card to-card space-y-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-primary font-bold inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Install to settle
            </p>
            <h2 className="font-display text-xl font-bold mt-1 leading-snug">
              Get SplitEasy — track groups, settle in 2 taps. No real money moves.
            </h2>
          </div>

          <ul className="text-xs space-y-1.5 text-muted-foreground">
            <li className="inline-flex items-center gap-2">
              <Zap className="w-3.5 h-3.5 text-primary shrink-0" /> Track shared expenses across trips, games & flatmates
            </li>
            <li className="inline-flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5 text-primary shrink-0" /> Splitwise-style ledger — debits & credits only, no cards
            </li>
            <li className="inline-flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" /> Free, instant, works offline
            </li>
          </ul>

          <Button
            onClick={handleInstall}
            className="w-full rounded-full text-base font-bold h-12"
            data-testid="scan-landing-install"
          >
            <Download className="w-5 h-5 mr-1.5" />
            Install SplitEasy
          </Button>

          {/* iOS / non-Chrome fallback hint */}
          {showIosHint && (
            <div className="text-xs space-y-1.5 p-3 rounded-xl bg-muted/60 text-muted-foreground" data-testid="scan-landing-ios-hint">
              {ios ? (
                <>
                  <p className="font-semibold text-foreground inline-flex items-center gap-1">
                    <Smartphone className="w-3.5 h-3.5" /> On iPhone / iPad
                  </p>
                  <p>1. Tap <Share2 className="inline w-3 h-3" /> in Safari's bottom bar.</p>
                  <p>2. Scroll → <strong>Add to Home Screen</strong> → <strong>Add</strong>.</p>
                </>
              ) : (
                <p>Open this page in Chrome and use the browser menu's <strong>Install App</strong> option.</p>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 my-1">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <Link to={`/login?redirect=${returnPath}`} className="block" data-testid="scan-landing-signin-cta">
            <Button variant="outline" className="w-full rounded-full">
              I already have an account
            </Button>
          </Link>
          <Link to={`/signup?redirect=${returnPath}`} className="block text-center text-xs text-muted-foreground hover:text-foreground" data-testid="scan-landing-signup-cta">
            New here? Create a free account →
          </Link>
        </Card>
      </motion.div>

      <p className="text-xs text-muted-foreground mt-6 text-center max-w-md">
        SplitEasy is a ledger — it never moves real money. Settling here updates a shared debt record both of you can see.
      </p>
    </div>
  );
}
